import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Image,
  Spin,
  Table,
  Tag,
  Empty,
  Modal,
  message,
  Popconfirm,
  Tooltip,
  Avatar,
} from 'antd';
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  BarChartOutlined,
  UserOutlined,
  CheckCircleFilled,
  CloseOutlined,
  DeleteOutlined,
  ReloadOutlined,
  PlayCircleOutlined,
  EditOutlined,
  ClockCircleOutlined,
  KeyOutlined,
} from '@ant-design/icons';
import PagesWrapper from '../../PagesWrapper';
import surveysService, { ChoiceType } from '../../services/surveysService';
import styles from './SurveyResponses.module.css';

const SurveyResponses = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [surveyName, setSurveyName] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [participantDetail, setParticipantDetail] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const fetchParticipants = useCallback(async () => {
    try {
      setLoading(true);
      const data = await surveysService.getSurveyParticipants(id);
      setParticipants(data.participants || []);
      setSurveyName(data.surveyName || '');
    } catch (error) {
      message.error(t('surveys.participantsError', 'Failed to load participants'));
      navigate('/surveys');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, t]);

  const fetchParticipantDetail = useCallback(async (participantId) => {
    try {
      setLoadingDetail(true);
      const data = await surveysService.getParticipantDetail(id, participantId);
      setParticipantDetail(data);
    } catch (error) {
      message.error(t('surveys.participantDetailError', 'Failed to load participant details'));
    } finally {
      setLoadingDetail(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchParticipants();
  }, [fetchParticipants]);

  const handleParticipantSelect = (participant) => {
    setModalOpen(true);
    fetchParticipantDetail(participant.id);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setParticipantDetail(null);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const result = await surveysService.downloadSurveyExport(id, surveyName || 'survey');
      if (result.success) {
        message.success(t('surveys.exportSuccess', 'Export downloaded successfully'));
      } else {
        message.error(t('surveys.exportError', 'Failed to export data'));
      }
    } catch (error) {
      message.error(t('surveys.exportError', 'Failed to export data'));
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteParticipant = async (participantId) => {
    try {
      setDeleting(true);
      await surveysService.deleteParticipant(id, participantId);
      message.success(t('surveys.participantDeleted', 'Participant deleted successfully'));
      setSelectedRowKeys(prev => prev.filter(key => key !== participantId));
      fetchParticipants();
    } catch (error) {
      message.error(t('surveys.deleteError', 'Failed to delete participant'));
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRowKeys.length === 0) return;
    
    try {
      setDeleting(true);
      const result = await surveysService.deleteParticipantsBulk(id, selectedRowKeys);
      message.success(t('surveys.participantsDeleted', '{{count}} participants deleted successfully', { count: result.deletedCount }));
      setSelectedRowKeys([]);
      fetchParticipants();
    } catch (error) {
      message.error(t('surveys.deleteError', 'Failed to delete participants'));
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const columns = [
    {
      title: t('surveys.participant', 'Participant'),
      key: 'participant',
      width: 450,
      render: (_, record) => (
        <div className={styles.participantCell}>
          {record.userId ? (
            // Registered user
            <div className={styles.userInfo}>
              <Avatar 
                size={32} 
                icon={<UserOutlined />}
                className={styles.userAvatar}
              />
              <div className={styles.userDetails}>
                <span className={styles.userName}>
                  {[record.firstName, record.lastName].filter(Boolean).join(' ') || record.userDisplayName || record.participantIdentifier}
                </span>
                {record.userEmail && (
                  <span className={styles.userEmail}>
                    {record.userEmail}
                  </span>
                )}
                {record.participantIdentifier && (
                  <span className={styles.userKey}>
                    <KeyOutlined /> {record.participantIdentifier}
                  </span>
                )}
              </div>
            </div>
          ) : (
            // Anonymous user with key
            record.participantIdentifier ? (
              <div className={styles.userInfo}>
                <Avatar 
                  size={32} 
                  icon={<KeyOutlined />}
                  className={styles.anonymousAvatar}
                />
                <div className={styles.userDetails}>
                  <span className={styles.userName}>{record.participantIdentifier}</span>
                </div>
              </div>
            ) : (
              // Fully anonymous
              <div className={styles.userInfo}>
                <Avatar 
                  size={32} 
                  icon={<UserOutlined />}
                  className={styles.anonymousAvatar}
                />
                <span className={styles.anonymousLabel}>
                  {t('surveys.anonymous', 'Anonymous')}
                </span>
              </div>
            )
          )}
        </div>
      ),
    },
    {
      title: t('surveys.type', 'Type'),
      key: 'type',
      width: 110,
      render: (_, record) => (
        <Tag color={record.userId ? 'blue' : 'default'}>
          {record.userId ? t('surveys.registered', 'Registered') : t('surveys.anonymous', 'Anonymous')}
        </Tag>
      ),
    },
    {
      title: t('surveys.status', 'Status'),
      key: 'status',
      width: 110,
      render: (_, record) => (
        <Tag 
          color={record.isCompleted ? 'green' : 'orange'}
          className={styles.statusTag}
        >
          {record.isCompleted ? t('surveys.completed', 'Completed') : t('surveys.inProgress', 'In Progress')}
        </Tag>
      ),
    },
    {
      title: t('surveys.startedAt', 'Started'),
      dataIndex: 'startedAt',
      key: 'startedAt',
      width: 170,
      render: (text) => formatDate(text),
    },
    {
      title: t('surveys.completedAt', 'Completed'),
      dataIndex: 'completedAt',
      key: 'completedAt',
      width: 170,
      render: (text) => formatDate(text),
    },
    {
      title: t('surveys.duration', 'Duration'),
      dataIndex: 'duration',
      key: 'duration',
      width: 100,
      render: (text) => (
        <span className={styles.durationCell}>
          {text ? (
            <>
              <ClockCircleOutlined className={styles.durationIcon} />
              {text}
            </>
          ) : '-'}
        </span>
      ),
    },
    {
      title: t('common.actions', 'Actions'),
      key: 'actions',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <Popconfirm
          title={t('surveys.deleteParticipantConfirm', 'Delete this participant?')}
          description={t('surveys.deleteParticipantDesc', 'This will permanently delete all responses from this participant.')}
          onConfirm={(e) => {
            e?.stopPropagation();
            handleDeleteParticipant(record.id);
          }}
          onCancel={(e) => e?.stopPropagation()}
          okText={t('common.delete', 'Delete')}
          cancelText={t('common.cancel', 'Cancel')}
          okButtonProps={{ danger: true }}
        >
          <Tooltip title={t('common.delete', 'Delete')}>
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => e.stopPropagation()}
              loading={deleting}
              size="small"
            />
          </Tooltip>
        </Popconfirm>
      ),
    },
  ];

  // Render a single choice option showing selection status
  const renderChoiceOption = (choice, isMultiple) => {
    const isSelected = choice.isSelected;
    
    switch (choice.choiceType) {
      case ChoiceType.Range:
        const rangeMin = choice.rangeMin ?? 0;
        const rangeMax = choice.rangeMax ?? 100;
        const value = choice.numericValue;
        const hasValue = value != null;
        const percentage = hasValue && rangeMax !== rangeMin 
          ? Math.round(((value - rangeMin) / (rangeMax - rangeMin)) * 100) 
          : 0;
        
        return (
          <div key={choice.id} className={`${styles.choiceOption} ${styles.rangeChoice} ${hasValue ? styles.selected : ''}`}>
            {choice.text && <div className={styles.rangeChoiceLabel}>{choice.text}</div>}
            <div className={styles.rangeAnswerDisplay}>
              <div className={styles.rangeValueBig}>
                {hasValue ? value : '-'}
              </div>
              <div className={styles.rangeContext}>
                <div className={styles.rangeBar}>
                  <div 
                    className={styles.rangeBarFill} 
                    style={{ width: `${percentage}%` }}
                  />
                  {hasValue && (
                    <div 
                      className={styles.rangeBarMarker} 
                      style={{ left: `${percentage}%` }}
                    />
                  )}
                </div>
                <div className={styles.rangeLabels}>
                  <span className={styles.rangeMinLabel}>
                    {choice.rangeMinLabel || rangeMin}
                  </span>
                  <span className={styles.rangeMaxLabel}>
                    {choice.rangeMaxLabel || rangeMax}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );

      case ChoiceType.FreeText:
        const hasText = choice.textValue && choice.textValue.trim();
        return (
          <div key={choice.id} className={`${styles.choiceOption} ${styles.freeTextChoice} ${hasText ? styles.selected : ''}`}>
            {choice.text && <div className={styles.freeTextChoiceLabel}>{choice.text}</div>}
            <div className={styles.freeTextAnswer}>
              {hasText ? `"${choice.textValue}"` : <span className={styles.noResponse}>{t('surveys.noResponse', 'No response')}</span>}
            </div>
          </div>
        );

      default: // Normal choice
        return (
          <div 
            key={choice.id} 
            className={`${styles.choiceOption} ${styles.normalChoice} ${isSelected ? styles.selected : ''}`}
          >
            <div className={styles.choiceSelector}>
              {isSelected ? (
                <CheckCircleFilled className={styles.selectedIcon} />
              ) : (
                <div className={isMultiple ? styles.uncheckedBox : styles.uncheckedRadio} />
              )}
            </div>
            <div className={styles.choiceContent}>
              {choice.imageUrl && (
                <Image src={choice.imageUrl} alt="" className={styles.choiceImage} />
              )}
              <span className={styles.choiceText}>{choice.text || t('surveys.unknownChoice', 'Unknown')}</span>
            </div>
          </div>
        );
    }
  };

  // Render all choices for a question
  const renderAllChoices = (answer) => {
    const choices = answer.allChoices || [];
    const isMultiple = answer.allowMultipleChoices;
    
    if (choices.length === 0) {
      return (
        <div className={styles.noChoices}>
          {t('surveys.noChoicesAvailable', 'No choices available')}
        </div>
      );
    }

    return (
      <div className={styles.choicesGrid}>
        {choices.map(choice => renderChoiceOption(choice, isMultiple))}
      </div>
    );
  };

  const renderModalContent = () => {
    if (loadingDetail) {
      return (
        <div className={styles.modalLoading}>
          <Spin size="large" />
        </div>
      );
    }

    if (!participantDetail) {
      return (
        <div className={styles.emptyState}>
          <Empty description={t('surveys.noResponses', 'No response data')} />
        </div>
      );
    }

    return (
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <div className={styles.participantInfo}>
            <h2>
              {participantDetail.isRegisteredUser ? (
                <>
                  <UserOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                  {participantDetail.userName || participantDetail.participantIdentifier}
                </>
              ) : (
                participantDetail.participantIdentifier || t('surveys.anonymous', 'Anonymous')
              )}
            </h2>
            <div className={styles.participantMeta}>
              <span>{t('surveys.startedAt', 'Started')}: {formatDate(participantDetail.startedAt)}</span>
              {participantDetail.completedAt && (
                <span>{t('surveys.completedAt', 'Completed')}: {formatDate(participantDetail.completedAt)}</span>
              )}
              {participantDetail.duration && (
                <span>{t('surveys.duration', 'Duration')}: {participantDetail.duration}</span>
              )}
            </div>
          </div>
          <Tag 
            color={participantDetail.isCompleted ? 'green' : 'orange'}
            style={{ fontSize: 14, padding: '4px 12px' }}
          >
            {participantDetail.isCompleted ? t('surveys.completed', 'Completed') : t('surveys.inProgress', 'In Progress')}
          </Tag>
        </div>

        {participantDetail.answers && participantDetail.answers.length > 0 ? (
          <div className={styles.answersList}>
            {participantDetail.answers.map((answer, idx) => (
              <div 
                key={answer.questionId || idx} 
                className={`${styles.answerCard} ${answer.questionWasDeleted ? styles.deleted : ''}`}
              >
                <div className={styles.answerQuestion}>
                  <span className={styles.questionNumber}>Q{idx + 1}</span>
                  <div className={styles.questionContent}>
                    {answer.questionName && (
                      <span className={styles.questionName}>{answer.questionName}</span>
                    )}
                    <span className={styles.questionText}>
                      {answer.questionBody}
                      {answer.questionWasDeleted && (
                        <Tag color="red" style={{ marginLeft: 8 }}>{t('surveys.deleted', 'Deleted')}</Tag>
                      )}
                    </span>
                    {answer.questionImageUrl && (
                      <Image 
                        src={answer.questionImageUrl} 
                        alt="" 
                        className={styles.questionImage}
                      />
                    )}
                  </div>
                </div>
                
                <div className={styles.answerContent}>
                  {renderAllChoices(answer)}
                  
                  {answer.choiceWasDeleted && (
                    <span className={styles.deletedNote}>
                      {t('surveys.choiceDeletedNote', 'Note: This choice has been deleted from the survey')}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Empty description={t('surveys.noAnswers', 'No answers recorded')} />
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <PagesWrapper>
        <div className={styles.loadingContainer}>
          <Spin size="large" />
        </div>
      </PagesWrapper>
    );
  }

  return (
    <PagesWrapper>
      <div className={styles.responsesContainer}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/surveys')}>
              {t('common.back', 'Back')}
            </Button>
            <h1>{surveyName} - {t('surveys.responses', 'Responses')}</h1>
          </div>
          <div className={styles.headerActions}>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchParticipants}
              loading={loading}
            >
              {t('common.refresh', 'Refresh')}
            </Button>
            <Button
              icon={<PlayCircleOutlined />}
              onClick={() => window.open(`/survey/${id}`, '_blank')}
            >
              {t('surveys.openSurvey', 'Open Survey')}
            </Button>
            <Button
              icon={<EditOutlined />}
              onClick={() => navigate(`/surveys/${id}`)}
            >
              {t('surveys.openEditor', 'Open Editor')}
            </Button>
            <Button
              icon={<BarChartOutlined />}
              onClick={() => navigate(`/surveys/${id}/statistics`)}
            >
              {t('surveys.viewStatistics', 'View Statistics')}
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExport}
              loading={exporting}
            >
              {t('surveys.exportCSV', 'Export CSV')}
            </Button>
          </div>
        </div>

        <div className={styles.tableSection}>
          <div className={styles.tableSectionHeader}>
            <h3 className={styles.sectionTitle}>{t('surveys.participants', 'Participants')} ({participants.length})</h3>
            {selectedRowKeys.length > 0 && (
              <Popconfirm
                title={t('surveys.bulkDeleteConfirm', 'Delete {{count}} participants?', { count: selectedRowKeys.length })}
                description={t('surveys.bulkDeleteDesc', 'This will permanently delete all responses from selected participants.')}
                onConfirm={handleBulkDelete}
                okText={t('common.delete', 'Delete')}
                cancelText={t('common.cancel', 'Cancel')}
                okButtonProps={{ danger: true }}
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  loading={deleting}
                >
                  {t('surveys.deleteSelected', 'Delete Selected')} ({selectedRowKeys.length})
                </Button>
              </Popconfirm>
            )}
          </div>
          <Table
            dataSource={participants}
            columns={columns}
            rowKey="id"
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
              getCheckboxProps: () => ({ disabled: deleting }),
            }}
            onRow={(record) => ({
              onClick: () => handleParticipantSelect(record),
              style: { cursor: 'pointer' },
            })}
            pagination={{ pageSize: 10 }}
            loading={loading || deleting}
          />
        </div>

        <Modal
          open={modalOpen}
          onCancel={handleCloseModal}
          footer={null}
          width={800}
          className={styles.responseModal}
          title={t('surveys.participantResponses', 'Participant Responses')}
          closeIcon={<CloseOutlined />}
        >
          {renderModalContent()}
        </Modal>
      </div>
    </PagesWrapper>
  );
};

export default SurveyResponses;
